import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Copy, Trash2, Upload, Users, MessageSquare, Clock, FileText } from 'lucide-react';
import type { WorkflowNodeData } from '@/types/workflow';
import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useParams } from 'react-router-dom';
import type { WorkflowChannel } from '@/types';

interface Props {
  selectedNode: string | null;
  selectedNodeType: string | null;
  channel: WorkflowChannel;
  nodeData: WorkflowNodeData | null;
  onUpdateNodeData: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
  onDuplicateNode?: (nodeId: string) => void;
  onDeleteNode?: (nodeId: string) => void;
}

type SegmentOption = {
  id: number;
  name: string;
};

export function BroadcastPropertiesPanel({
  selectedNode,
  selectedNodeType,
  channel,
  nodeData,
  onUpdateNodeData,
  onDuplicateNode,
  onDeleteNode,
}: Props) {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const [uploadingImage, setUploadingImage] = useState(false);
  const [segments, setSegments] = useState<SegmentOption[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (channel === 'facebook_message') {
      setSegments([]);
      setLoadingSegments(false);
      return;
    }

    if (!projectSlug) return;

    let cancelled = false;

    const loadSegments = async () => {
      setLoadingSegments(true);

      try {
        const response = await fetch(`/api/projects/${projectSlug}/segments`, {
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('Falha ao carregar segmentos');
        }

        const data = await response.json();
        if (!cancelled) {
          setSegments(Array.isArray(data.segments) ? data.segments : []);
        }
      } catch (_error) {
        if (!cancelled) {
          setSegments([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingSegments(false);
        }
      }
    };

    loadSegments();

    return () => {
      cancelled = true;
    };
  }, [channel, projectSlug]);

  const resolveAbsoluteUrl = (value: string): string => {
    if (!value) return value;

    try {
      return new URL(value, window.location.origin).toString();
    } catch (_error) {
      return value;
    }
  };

  const handlePushImageUpload = async (file: File) => {
    if (!selectedNode) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const projectSlug = window.location.pathname.split('/')[1];
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

      const response = await fetch(`/api/projects/${projectSlug}/workflow_images`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken || '',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.errors?.[0] || 'Falha ao enviar imagem');
      }

      const data = await response.json();
      const absoluteImageUrl = resolveAbsoluteUrl(data.workflow_image.url || '');
      onUpdateNodeData(selectedNode, { pushImageUrl: absoluteImageUrl });
    } catch (error) {
      toast({
        title: 'Erro no upload',
        description: error instanceof Error ? error.message : 'Falha ao enviar imagem',
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  // No node selected
  if (!selectedNode || !nodeData) {
    return (
      <div className="w-72 shrink-0 border-l bg-background flex flex-col">
        <div className="px-4 pt-4 pb-3 border-b">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Propriedades</p>
        </div>
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <p className="text-sm text-muted-foreground">Selecione um nó para editar</p>
        </div>
      </div>
    );
  }

  // Determine node type
  const nodeType = selectedNodeType;
  const isFacebookWorkflow = channel === 'facebook_message';
  const audienceTargetType = isFacebookWorkflow ? 'all' : (nodeData.targetType || 'all');

  const selectedSegmentIds = Array.isArray(nodeData.selectedSegmentIds)
    ? nodeData.selectedSegmentIds.map((value) => Number(value)).filter((value) => Number.isFinite(value))
    : [];

  const updateSelectedSegments = (nextIds: number[]) => {
    const selectedSegments = segments
      .filter((segment) => nextIds.includes(segment.id))
      .map((segment) => ({ id: segment.id, name: segment.name }));

    onUpdateNodeData(selectedNode, {
      selectedSegmentIds: nextIds,
      selectedSegments,
    });
  };

  return (
    <div className="w-72 shrink-0 border-l bg-background overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          {nodeType === 'audience' && <Users className="w-3.5 h-3.5 text-muted-foreground" />}
          {nodeType === 'message' && <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />}
          {nodeType === 'facebook_message' && <FileText className="w-3.5 h-3.5 text-muted-foreground" />}
          {nodeType === 'delay' && <Clock className="w-3.5 h-3.5 text-muted-foreground" />}
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            {nodeType === 'audience' && 'Audiência'}
            {nodeType === 'message' && 'Push'}
            {nodeType === 'facebook_message' && 'Messenger'}
            {nodeType === 'delay' && 'Aguardar'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {onDuplicateNode && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onDuplicateNode(selectedNode)}
              title="Duplicar nó"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          )}
          {onDeleteNode && nodeType !== 'audience' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={() => onDeleteNode(selectedNode)}
              title="Excluir nó"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Audience Node */}
        {nodeType === 'audience' && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs mb-2">Tipo de público</Label>
              <Select
                value={audienceTargetType}
                onValueChange={(value: 'all' | 'tags' | 'segment') =>
                  onUpdateNodeData(selectedNode, { targetType: value })
                }
                disabled={isFacebookWorkflow}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os contatos</SelectItem>
                  {!isFacebookWorkflow && <SelectItem value="tags">Por tags</SelectItem>}
                  {!isFacebookWorkflow && <SelectItem value="segment">Segmento comportamental</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {isFacebookWorkflow && (
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs font-medium">Canal Messenger</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Workflows de Messenger usam a lista de contatos inscritos do projeto. Segmentos comportamentais de push ficam ocultos aqui.
                </p>
              </div>
            )}

            {!isFacebookWorkflow && nodeData.targetType === 'tags' && (
              <div>
                <Label className="text-xs mb-2">Tags selecionadas</Label>
                <p className="text-xs text-muted-foreground">
                  Selecione tags para filtrar contatos (funcionalidade completa em breve)
                </p>
              </div>
            )}

            {!isFacebookWorkflow && nodeData.targetType === 'segment' && (
              <div>
                <Label className="text-xs mb-2">Segmento</Label>
                {loadingSegments ? (
                  <p className="text-xs text-muted-foreground">Carregando segmentos...</p>
                ) : segments.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Nenhum segmento criado ainda. Crie segmentos na página Segments.
                  </p>
                ) : (
                  <div className="space-y-2 rounded-md border p-3">
                    {segments.map((segment) => (
                      <label key={segment.id} className="flex items-center gap-2 text-xs">
                        <Checkbox
                          checked={selectedSegmentIds.includes(segment.id)}
                          onCheckedChange={(checked) => {
                            const isChecked = checked === true;
                            if (isChecked) {
                              updateSelectedSegments(Array.from(new Set([ ...selectedSegmentIds, segment.id ])));
                              return;
                            }

                            updateSelectedSegments(selectedSegmentIds.filter((id) => id !== segment.id));
                          }}
                        />
                        {segment.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(nodeData.contactCount || 0) > 0 && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Contatos alcançados</p>
                <p className="text-2xl font-bold">{nodeData.contactCount}</p>
              </div>
            )}
          </div>
        )}

        {/* Message Node */}
        {nodeType === 'message' && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs mb-2">Modo</Label>
              <Select
                value={nodeData.messageMode || 'custom'}
                onValueChange={(value: 'custom' | 'template') =>
                  onUpdateNodeData(selectedNode, { messageMode: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Push personalizado</SelectItem>
                  <SelectItem value="template">Template aprovado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {nodeData.messageMode === 'template' ? (
              <div>
                <Label className="text-xs mb-2">Template</Label>
                <p className="text-xs text-muted-foreground">
                  Seletor de templates (funcionalidade completa em breve)
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Título</Label>
                    <Input
                      value={nodeData.pushTitle || ''}
                      onChange={(e) =>
                        onUpdateNodeData(selectedNode, { pushTitle: e.target.value })
                      }
                      placeholder="Título da notificação"
                      className="text-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Descrição</Label>
                    <Textarea
                      value={nodeData.pushDescription || ''}
                      onChange={(e) =>
                        onUpdateNodeData(selectedNode, { pushDescription: e.target.value })
                      }
                      placeholder="Texto que será exibido no push"
                      rows={3}
                      className="text-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">URL de destino</Label>
                    <Input
                      value={nodeData.pushUrl || ''}
                      onChange={(e) =>
                        onUpdateNodeData(selectedNode, { pushUrl: e.target.value })
                      }
                      placeholder="https://exemplo.com/pagina"
                      className="text-sm"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      UTMs adicionados automaticamente (utm_source, utm_campaign)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Imagem</Label>
                    {nodeData.pushImageUrl ? (
                      <div className="relative">
                        <img
                          src={nodeData.pushImageUrl}
                          alt="Preview da imagem do push"
                          className="w-full rounded-lg border"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => onUpdateNodeData(selectedNode, { pushImageUrl: '' })}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <Upload className="w-7 h-7 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground mb-2">
                          Envie uma imagem para exibir no push
                        </p>
                        <Input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          disabled={uploadingImage}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handlePushImageUpload(file);
                          }}
                        />
                        {uploadingImage && (
                          <p className="text-xs text-muted-foreground mt-2">Enviando...</p>
                        )}
                      </div>
                    )}
                  </div>

                  <p className="text-[11px] text-muted-foreground">
                    Esse bloco configura exatamente o push enviado ao usuário: título, descrição, URL e imagem.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {nodeType === 'facebook_message' && (
          <div className="space-y-4">
            <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
              <div className="space-y-1">
                <Label className="text-xs">Nome do template</Label>
                <Input
                  value={nodeData.templateName || ''}
                  onChange={(e) =>
                    onUpdateNodeData(selectedNode, { templateName: e.target.value })
                  }
                  placeholder="ex.: order_update"
                  className="text-sm"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Idioma</Label>
                <Input
                  value={nodeData.templateLanguage || 'pt_BR'}
                  onChange={(e) =>
                    onUpdateNodeData(selectedNode, { templateLanguage: e.target.value })
                  }
                  placeholder="pt_BR"
                  className="text-sm"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Parâmetros do corpo</Label>
                <Textarea
                  value={nodeData.templateParams || ''}
                  onChange={(e) =>
                    onUpdateNodeData(selectedNode, { templateParams: e.target.value })
                  }
                  placeholder={'{{first_name}}\n{{email}}'}
                  rows={4}
                  className="text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Use um valor por linha ou separado por vírgulas. Variáveis como <code>{"{{first_name}}"}</code> serão substituídas com dados do contato.
                </p>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Message tag</Label>
                <Select
                  value={nodeData.messageTag || 'RESPONSE'}
                  onValueChange={(value) =>
                    onUpdateNodeData(selectedNode, { messageTag: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RESPONSE">RESPONSE</SelectItem>
                    <SelectItem value="UTILITY">UTILITY</SelectItem>
                    <SelectItem value="CONFIRMED_EVENT_UPDATE">CONFIRMED_EVENT_UPDATE</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Botões URL</Label>
                  <Input
                    type="number"
                    min="0"
                    value={nodeData.urlButtonsCount ?? 0}
                    onChange={(e) =>
                      onUpdateNodeData(selectedNode, { urlButtonsCount: Math.max(0, Number(e.target.value) || 0) })
                    }
                    className="text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Campanha</Label>
                  <Input
                    value={nodeData.campaignName || ''}
                    onChange={(e) =>
                      onUpdateNodeData(selectedNode, { campaignName: e.target.value })
                    }
                    placeholder="newsletter_marco"
                    className="text-sm"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-md border bg-background p-3">
                <Checkbox
                  checked={nodeData.bodyHasVariables === true}
                  onCheckedChange={(checked) =>
                    onUpdateNodeData(selectedNode, { bodyHasVariables: checked === true })
                  }
                />
                <div>
                  <Label className="text-xs">O corpo do template tem variáveis</Label>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Ative quando os parâmetros acima devem ser substituídos com dados do contato antes do envio.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delay Node */}
        {nodeType === 'delay' && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs mb-2">Tempo de espera</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="1"
                  value={nodeData.delay || '5'}
                  onChange={(e) =>
                    onUpdateNodeData(selectedNode, { delay: e.target.value })
                  }
                  className="flex-1"
                />
                <Select
                  value={nodeData.unit || 'minutos'}
                  onValueChange={(value: 'segundos' | 'minutos' | 'horas' | 'dias') =>
                    onUpdateNodeData(selectedNode, { unit: value })
                  }
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="segundos">Segundos</SelectItem>
                    <SelectItem value="minutos">Minutos</SelectItem>
                    <SelectItem value="horas">Horas</SelectItem>
                    <SelectItem value="dias">Dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Duração calculada</p>
              <p className="font-medium text-sm">
                {calculateDuration(parseInt(nodeData.delay || '5'), nodeData.unit || 'minutos')}
              </p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

// Helper function to calculate duration in human-readable format
function calculateDuration(delay: number, unit: string): string {
  const seconds = delay * (
    unit === 'segundos' ? 1 :
    unit === 'minutos' ? 60 :
    unit === 'horas' ? 3600 :
    unit === 'dias' ? 86400 :
    1
  );

  if (seconds < 60) return `${seconds} segundo${seconds !== 1 ? 's' : ''}`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minuto${Math.floor(seconds / 60) !== 1 ? 's' : ''}`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hora${Math.floor(seconds / 3600) !== 1 ? 's' : ''}`;
  return `${Math.floor(seconds / 86400)} dia${Math.floor(seconds / 86400) !== 1 ? 's' : ''}`;
}
